import styled from '@emotion/styled';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';

import { getContrastTextColor, useTheme } from '@bai/utils/theme';

export enum ButtonVariant {
  ROUNDED = 'ROUNDED',
  SQUARE = 'SQUARE',
  SQUARE_INVERTED = 'SQUARE_INVERTED',
  SUBMIT = 'SUBMIT',
  TEXT = 'TEXT',
  OUTLINED = 'OUTLINED',
}

export interface ButtonProps extends Omit<
  MuiButtonProps,
  'variant' | 'buttonVariant'
> {
  buttonVariant?: ButtonVariant;
  variant?: ButtonVariant;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
  useBrandColor?: boolean;
  specificColorToUse?: string;
  textColor?: string;
}

interface StyledButtonProps {
  theme: ReturnType<typeof useTheme>;
  textColor?: string;
  $buttonVariant: ButtonVariant;
  $mainColor: string;
  $contrastTextColor: string;
  $border1: string;
  $border2: string | false | undefined;
  $hoverBg: string;
  $hoverColor: string;
  $color: string;
  $bgColor: string;
  $borderRadius: string;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !(prop as string).startsWith('$'),
})<StyledButtonProps>`
  padding: ${({ $buttonVariant }) =>
    $buttonVariant === ButtonVariant.TEXT ? '10px 15px' : '10px 50px'};
  min-width: fit-content;
  height: 4.2rem;
  text-transform: capitalize;
  text-decoration: ${({ $buttonVariant }) =>
    $buttonVariant === ButtonVariant.TEXT ? 'underline' : ''};
  font-family: ${({ $buttonVariant }) =>
    $buttonVariant === ButtonVariant.TEXT ? 'Verdana,Arial,sans-serif' : ''};
  font-size: ${({ $buttonVariant }) =>
    $buttonVariant === ButtonVariant.TEXT ? '1.6rem' : ''};
  border: ${({ $border1 }) => $border1};
  border: ${({ $border2 }) => $border2};
  &:active:hover {
    color: #333;
    background-color: #d4d4d4;
  }
  &:active {
    color: ${({
      textColor,
      $mainColor,
      $contrastTextColor,
      $buttonVariant,
    }) => {
      if (
        textColor &&
        ['#FEFEFE', '#FFFFFF'].includes($mainColor.toUpperCase())
      ) {
        // EA-17279
        return textColor;
      }
      return $buttonVariant === ButtonVariant.TEXT
        ? 'black'
        : $contrastTextColor;
    }} !important;
    box-shadow: inset 0 3px 5px rgb(0 0 0 / 13%);
  }
  &:hover {
    background-color: ${({ $hoverBg }) => $hoverBg};
    text-decoration: ${({ $buttonVariant }) =>
      $buttonVariant === ButtonVariant.TEXT ? 'underline' : ''};
    color: ${({ $hoverColor }) => $hoverColor};
  }
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: ${({ $borderRadius }) => $borderRadius};
`;

export function Button({
  variant = ButtonVariant.ROUNDED,

  buttonVariant: _buttonVariant,
  onClick,
  children,
  useBrandColor = false,
  specificColorToUse,
  ...rest
}: ButtonProps) {
  const theme = useTheme();
  const mainColor =
    specificColorToUse ||
    (useBrandColor && theme?.branding?.primaryColor) ||
    theme?.palette.primary.main;
  const contrastTextColor = getContrastTextColor(mainColor);
  const { textColor } = rest;

  const buttonVariant = variant;

  const getBorder1 = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return '';
    }
    return buttonVariant === ButtonVariant.SQUARE_INVERTED
      ? '2px solid black'
      : '1px solid black';
  };

  const getBorder2 = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return '';
    }
    return buttonVariant === ButtonVariant.SUBMIT && theme.spacing(0);
  };

  const getHoverBackgroundColor = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return 'transparent';
    }
    return buttonVariant === ButtonVariant.SQUARE_INVERTED
      ? contrastTextColor
      : mainColor;
  };

  const getHoverColor = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return theme.palette.secondary.contrastText;
    }
    if (buttonVariant === ButtonVariant.OUTLINED) {
      return contrastTextColor;
    }
    return '';
  };

  const getColor = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return '#000';
    }
    return buttonVariant === ButtonVariant.SQUARE_INVERTED
      ? mainColor
      : contrastTextColor;
  };

  const getBackgroundColor = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return 'transparent';
    }
    if (buttonVariant === ButtonVariant.OUTLINED) {
      return '#fff';
    }
    return buttonVariant === ButtonVariant.SQUARE_INVERTED
      ? `${contrastTextColor} !important`
      : `${mainColor} !important`;
  };

  const getBorderRadius = () => {
    if (buttonVariant === ButtonVariant.TEXT) {
      return '';
    }
    return buttonVariant === ButtonVariant.ROUNDED
      ? theme?.spacing(1)
      : theme?.spacing(0);
  };

  return (
    <StyledButton
      disableRipple={variant === ButtonVariant.TEXT}
      variant={variant === ButtonVariant.TEXT ? 'text' : 'contained'}
      theme={theme}
      onClick={onClick}
      disableElevation
      role="button"
      $buttonVariant={buttonVariant}
      $mainColor={mainColor}
      $contrastTextColor={contrastTextColor}
      $border1={getBorder1()}
      $border2={getBorder2()}
      $hoverBg={getHoverBackgroundColor()}
      $hoverColor={getHoverColor()}
      $color={getColor()}
      $bgColor={getBackgroundColor()}
      $borderRadius={getBorderRadius()}
      {...rest}
    >
      {children}
    </StyledButton>
  );
}

export default Button;
